import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getAccessToken } from './auth';
import { capDisplayCell, capDisplayValue } from './capDisplayValue';

const CAP_API_URL = 'https://fp-api.focuspointintl.com/api/capContactsAndDevices';

const ONX_MAPS_PARENT = 'ONX MAPS';
const ONX_APP_DEVICE_NAME = 'onX App';

const isOnXAppDevice = (device) =>
  capDisplayValue(device?.devicename).toLowerCase() === ONX_APP_DEVICE_NAME.toLowerCase();

/** ONX MAPS members always show the onX App device; OXR DTC uses API devices only. */
const mergeDevicesForParent = (parent, apiDevices) => {
  const devices = Array.isArray(apiDevices) ? [...apiDevices] : [];
  if (parent !== ONX_MAPS_PARENT) return devices;
  if (devices.some(isOnXAppDevice)) return devices;
  return [{ devicename: ONX_APP_DEVICE_NAME }, ...devices];
};

const isMemberActive = (record) => {
  if (!record) return false;
  const today = new Date();
  const startDate = record.Start ? new Date(record.Start) : null;
  const endDate = record.End ? new Date(record.End) : null;
  return Boolean(startDate && endDate && today >= startDate && today <= endDate);
};

const CapContactsDrawer = ({ open, record, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [devices, setDevices] = useState([]);

  const accountNumber = capDisplayValue(record?.MembershipEXT);
  const parentCompany = record?.Parent;

  const loadCapData = useCallback(async () => {
    if (!accountNumber) {
      setError('No client account number is available for this member.');
      setContacts([]);
      setDevices([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error('Authentication failed');

      const url = `${CAP_API_URL}?accountNumber=${encodeURIComponent(accountNumber)}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          typeof body.message === 'string' && body.message.trim()
            ? body.message
            : 'Failed to load emergency contacts and devices';
        throw new Error(message);
      }

      setContacts(Array.isArray(body.contacts) ? body.contacts : []);
      setDevices(mergeDevicesForParent(parentCompany, body.devices));
    } catch (err) {
      console.error('CAP contacts/devices error:', err);
      setError(err.message || 'Failed to load emergency contacts and devices');
      setContacts([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [accountNumber, parentCompany]);

  useEffect(() => {
    if (!open || !record) return;
    loadCapData();
  }, [open, record, loadCapData]);

  const memberName = [capDisplayValue(record?.First), capDisplayValue(record?.Last)]
    .filter(Boolean)
    .join(' ')
    .trim();

  const active = isMemberActive(record);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: '100vw', sm: 520 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ pr: 1 }}>
            <Typography variant="h6" component="h2" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Emergency contacts & devices
            </Typography>
            {record && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {memberName || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Client account: {accountNumber || '—'}
                  {record.Parent ? ` · ${record.Parent}` : ''}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.25, color: active ? 'success.main' : 'error.main', fontWeight: 500 }}
                >
                  {active ? 'Active' : 'Inactive'}
                </Typography>
              </>
            )}
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Emergency contacts
              </Typography>
              {contacts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No emergency contacts on file.
                </Typography>
              ) : (
                <TableContainer sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Relationship</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Language</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.map((contact, index) => {
                        const name = [capDisplayValue(contact.firstname), capDisplayValue(contact.lastname)]
                          .filter(Boolean)
                          .join(' ');
                        return (
                          <TableRow key={`contact-${index}`}>
                            <TableCell>{name || '—'}</TableCell>
                            <TableCell>{capDisplayCell(contact.relationship)}</TableCell>
                            <TableCell>{capDisplayCell(contact.phone)}</TableCell>
                            <TableCell>{capDisplayCell(contact.email)}</TableCell>
                            <TableCell>{capDisplayCell(contact.language)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Devices
              </Typography>
              {devices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No devices on file.
                </Typography>
              ) : (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Device</TableCell>
                        <TableCell>ESN</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Phone 2</TableCell>
                        <TableCell>Sat carrier</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {devices.map((device, index) => (
                        <TableRow key={`device-${index}`}>
                          <TableCell>{capDisplayCell(device.devicename)}</TableCell>
                          <TableCell>{capDisplayCell(device.esn)}</TableCell>
                          <TableCell>{capDisplayCell(device.phone)}</TableCell>
                          <TableCell>{capDisplayCell(device.phone2)}</TableCell>
                          <TableCell>{capDisplayCell(device.satcarrier)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default CapContactsDrawer;
